import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Plus, 
  X, 
  Link, 
  Video, 
  FileText, 
  Globe, 
  ExternalLink, 
  Youtube,
  Play,
  Linkedin,
  Github
} from "lucide-react";
import { cn } from "@/lib/utils";

export interface ResourceLink {
  id: string;
  type: "video" | "link" | "document" | "social";
  url: string;
  title?: string;
  platform?: string;
}

interface ChallengeResourceLinksProps {
  resources: ResourceLink[];
  onResourcesChange: (resources: ResourceLink[]) => void;
  readOnly?: boolean;
}

const resourceTypes = [
  { value: "video", label: "Video", icon: Video, placeholder: "https://youtube.com/watch?v=..." },
  { value: "link", label: "Website/Link", icon: Globe, placeholder: "https://example.com" },
  { value: "document", label: "Document", icon: FileText, placeholder: "https://docs.google.com/..." },
  { value: "social", label: "Social/Profile", icon: Link, placeholder: "https://linkedin.com/in/..." },
];

// Detect platform from URL
const detectPlatform = (url: string): string | undefined => {
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.includes("youtube.com") || lowerUrl.includes("youtu.be")) return "youtube";
  if (lowerUrl.includes("vimeo.com")) return "vimeo";
  if (lowerUrl.includes("loom.com")) return "loom";
  if (lowerUrl.includes("linkedin.com")) return "linkedin";
  if (lowerUrl.includes("github.com")) return "github";
  if (lowerUrl.includes("figma.com")) return "figma";
  if (lowerUrl.includes("notion.so") || lowerUrl.includes("notion.site")) return "notion";
  if (lowerUrl.includes("docs.google.com")) return "google-docs";
  if (lowerUrl.includes("drive.google.com")) return "google-drive";
  if (lowerUrl.includes("twitter.com") || lowerUrl.includes("x.com")) return "twitter";
  return undefined;
};

// Get platform-specific icon
const getPlatformIcon = (platform?: string, type?: string) => {
  switch (platform) {
    case "youtube":
    case "vimeo":
    case "loom":
      return Play;
    case "linkedin":
      return Linkedin;
    case "github":
      return Github;
    default:
      if (type === "video") return Video;
      if (type === "document") return FileText;
      if (type === "social") return Link;
      return Globe;
  }
};

// Get platform color
const getPlatformColor = (platform?: string, type?: string) => {
  switch (platform) {
    case "youtube":
      return "text-red-500 bg-red-500/10";
    case "vimeo":
      return "text-blue-400 bg-blue-400/10";
    case "loom":
      return "text-purple-500 bg-purple-500/10";
    case "linkedin":
      return "text-blue-600 bg-blue-600/10";
    case "github":
      return "text-foreground bg-secondary";
    case "figma":
      return "text-purple-600 bg-purple-600/10";
    case "notion":
      return "text-foreground bg-secondary";
    default:
      if (type === "video") return "text-purple-500 bg-purple-500/10";
      if (type === "document") return "text-blue-500 bg-blue-500/10";
      if (type === "social") return "text-green-500 bg-green-500/10";
      return "text-muted-foreground bg-secondary";
  }
};

// Get embeddable URL for videos
const getEmbedUrl = (url: string, platform?: string): string | null => {
  try {
    if (platform === "youtube") {
      const videoId = url.includes("youtu.be") 
        ? url.split("youtu.be/")[1]?.split(/[?#]/)[0]
        : new URL(url).searchParams.get("v");
      if (videoId) return `https://www.youtube.com/embed/${videoId}`;
    }
    if (platform === "vimeo") {
      const videoId = url.split("vimeo.com/")[1]?.split(/[?#]/)[0];
      if (videoId) return `https://player.vimeo.com/video/${videoId}`;
    }
    if (platform === "loom") {
      const videoId = url.split("/share/")[1]?.split(/[?#]/)[0];
      if (videoId) return `https://www.loom.com/embed/${videoId}`;
    }
  } catch {
    return null;
  }
  return null;
};

export const ChallengeResourceLinksInput = ({ 
  resources, 
  onResourcesChange 
}: ChallengeResourceLinksProps) => {
  const [newResource, setNewResource] = useState<Partial<ResourceLink>>({
    type: "link",
    url: "",
    title: "",
  });

  const handleAddResource = () => {
    if (!newResource.url?.trim()) return;

    const platform = detectPlatform(newResource.url);
    const resource: ResourceLink = {
      id: crypto.randomUUID(),
      type: newResource.type as ResourceLink["type"],
      url: newResource.url.trim(),
      title: newResource.title?.trim() || undefined,
      platform,
    };

    onResourcesChange([...resources, resource]);
    setNewResource({ type: "link", url: "", title: "" });
  };

  const handleRemoveResource = (id: string) => {
    onResourcesChange(resources.filter(r => r.id !== id));
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {/* Resource Type Selection */}
        <div className="grid grid-cols-4 gap-2">
          {resourceTypes.map((type) => (
            <button
              key={type.value}
              type="button"
              onClick={() => setNewResource({ ...newResource, type: type.value as ResourceLink["type"] })}
              className={cn(
                "flex flex-col items-center gap-1 p-2 rounded-lg border transition-colors",
                newResource.type === type.value
                  ? "border-primary bg-primary/5 text-primary"
                  : "border-border hover:border-primary/50 text-muted-foreground"
              )}
            >
              <type.icon className="w-4 h-4" />
              <span className="text-xs">{type.label}</span>
            </button>
          ))}
        </div>

        {/* URL Input */}
        <div className="flex gap-2">
          <div className="flex-1 space-y-2">
            <Input
              value={newResource.url || ""}
              onChange={(e) => setNewResource({ ...newResource, url: e.target.value })}
              placeholder={resourceTypes.find(t => t.value === newResource.type)?.placeholder}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddResource();
                }
              }}
            />
          </div>
        </div>

        {/* Optional Title */}
        <div className="flex gap-2">
          <Input
            value={newResource.title || ""}
            onChange={(e) => setNewResource({ ...newResource, title: e.target.value })}
            placeholder="Label (optional) - e.g., 'Product Demo Video'"
            className="flex-1"
          />
          <Button type="button" variant="outline" onClick={handleAddResource} disabled={!newResource.url?.trim()}>
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      </div>

      {/* Added Resources */}
      {resources.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Added Resources ({resources.length})</Label>
          <div className="space-y-2">
            {resources.map((resource) => {
              const Icon = getPlatformIcon(resource.platform, resource.type);
              const colorClass = getPlatformColor(resource.platform, resource.type);
              
              return (
                <div
                  key={resource.id}
                  className="flex items-center gap-3 p-3 bg-secondary/50 border border-border rounded-lg group"
                >
                  <div className={cn("p-2 rounded-lg", colorClass)}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {resource.title || resource.platform || resource.type}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{resource.url}</p>
                  </div>
                  <button
                    onClick={() => handleRemoveResource(resource.id)}
                    className="p-1 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Display component for viewing resources
export const ChallengeResourceLinksDisplay = ({ 
  resources 
}: { resources: ResourceLink[] }) => {
  const [expandedVideo, setExpandedVideo] = useState<string | null>(null);

  if (!resources || resources.length === 0) return null;

  const videoResources = resources.filter(r => r.type === "video");
  const otherResources = resources.filter(r => r.type !== "video");

  return (
    <div className="space-y-4">
      {/* Video Embeds */}
      {videoResources.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-semibold text-foreground flex items-center gap-2">
            <Video className="w-4 h-4 text-muted-foreground" />
            Videos ({videoResources.length})
          </h4>
          <div className="space-y-3">
            {videoResources.map((resource) => {
              const embedUrl = getEmbedUrl(resource.url, resource.platform);
              const Icon = getPlatformIcon(resource.platform, resource.type);
              const colorClass = getPlatformColor(resource.platform, resource.type);
              const isExpanded = expandedVideo === resource.id;

              return (
                <div key={resource.id} className="border border-border rounded-lg overflow-hidden">
                  {/* Video Header */}
                  <div className="flex items-center justify-between px-3 py-2 bg-secondary/50">
                    <div className="flex items-center gap-2">
                      <div className={cn("p-1.5 rounded", colorClass)}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium text-foreground">
                        {resource.title || resource.platform || "Video"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {embedUrl && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => setExpandedVideo(isExpanded ? null : resource.id)}
                        >
                          {isExpanded ? "Collapse" : "Preview"}
                        </Button>
                      )}
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 h-7 px-2 text-sm text-muted-foreground hover:text-foreground"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                  
                  {/* Video Embed */}
                  {isExpanded && embedUrl && (
                    <div className="aspect-video">
                      <iframe
                        src={embedUrl}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title={resource.title || "Video"}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Other Links */}
      {otherResources.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-semibold text-foreground flex items-center gap-2">
            <Link className="w-4 h-4 text-muted-foreground" />
            Resources & Links ({otherResources.length})
          </h4>
          <div className="grid sm:grid-cols-2 gap-2">
            {otherResources.map((resource) => {
              const Icon = getPlatformIcon(resource.platform, resource.type);
              const colorClass = getPlatformColor(resource.platform, resource.type);

              return (
                <a
                  key={resource.id}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-3 py-2.5 bg-secondary/50 border border-border rounded-lg hover:bg-secondary hover:border-primary/50 transition-colors group"
                >
                  <div className={cn("p-2 rounded-lg", colorClass)}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
                      {resource.title || resource.platform || resource.type}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {new URL(resource.url).hostname.replace("www.", "")}
                    </p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
